import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { ExternalLink, Check, X, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/verifications")({
  component: AdminVerifications,
});

interface Row {
  id: string;
  instagram_url: string | null;
  requested_at: string;
  user_id: string;
  port_id: string;
  profiles: { nickname: string | null } | null;
  ports: { name: string | null; watts_reward: number | null } | null;
}

function AdminVerifications() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("port_completions")
      .select("id, instagram_url, requested_at, user_id, port_id, profiles(nickname), ports(name, watts_reward)")
      .eq("status", "in_review")
      .order("requested_at", { ascending: true });
    if (error) toast.error(error.message);
    setRows((data as any) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const review = async (id: string, approve: boolean) => {
    setBusy(id);
    const fn = approve ? "admin_approve_port_completion" : "admin_reject_port_completion";
    const { error } = await supabase.rpc(fn, { completion_id: id });
    setBusy(null);
    if (error) { toast.error(error.message); return; }
    toast.success(approve ? "Aprobado" : "Rechazado");
    setRows((r) => r.filter((x) => x.id !== id));
  };

  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-2xl mb-1">Verificaciones</h1>
      <p className="text-sm text-muted-foreground mb-6">Solicitudes de coronación de puertos pendientes de revisión.</p>

      {loading ? (
        <div className="text-sm text-muted-foreground">Cargando…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-border bg-surface p-8 text-center text-sm text-muted-foreground">
          No hay solicitudes pendientes.
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.id} className="rounded-2xl border border-border bg-surface p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg text-foreground">
                    {r.ports?.name ?? "Puerto"}
                    {r.ports?.watts_reward != null && (
                      <span className="ml-2 inline-flex items-center gap-1 text-sm text-primary align-middle">
                        <Zap className="h-4 w-4" fill="currentColor" strokeWidth={0} />
                        {r.ports.watts_reward}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    @{r.profiles?.nickname ?? "rider"} · {new Date(r.requested_at).toLocaleString("es-ES", { timeZone: "Europe/Madrid" })}
                  </div>
                  {r.instagram_url && (
                    <a
                      href={r.instagram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary hover:underline break-all"
                    >
                      <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                      {r.instagram_url}
                    </a>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    disabled={busy === r.id}
                    onClick={() => review(r.id, true)}
                    className="press-scale inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                  >
                    <Check className="h-4 w-4" /> Aprobar
                  </button>
                  <button
                    disabled={busy === r.id}
                    onClick={() => review(r.id, false)}
                    className="press-scale inline-flex items-center gap-1.5 rounded-xl border border-border bg-background px-4 py-2 text-sm text-foreground disabled:opacity-50"
                  >
                    <X className="h-4 w-4" /> Rechazar
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
